import DxfParser from "dxf-parser";

const DEFAULT_THICKNESS_MM = 18;
const EPS = 1e-6;

const isFiniteNum = (n) => Number.isFinite(Number(n));

const point = (x, y) => ({x: Number(x) || 0, y: Number(y) || 0});

const nearlyEqual = (a, b) => Math.abs(a - b) <= EPS;

const polygonArea = (pts) => {
    if (!pts?.length || pts.length < 3) return 0;
    let sum = 0;
    for (let i = 0; i < pts.length; i++) {
        const j = (i + 1) % pts.length;
        sum += pts[i].x * pts[j].y - pts[j].x * pts[i].y;
    }
    return Math.abs(sum) * 0.5;
};

const expandBounds = (bounds, x, y) => {
    if (!isFiniteNum(x) || !isFiniteNum(y)) return;
    bounds.minX = Math.min(bounds.minX, x);
    bounds.minY = Math.min(bounds.minY, y);
    bounds.maxX = Math.max(bounds.maxX, x);
    bounds.maxY = Math.max(bounds.maxY, y);
};

const emptyBounds = () => ({
    minX: Infinity,
    minY: Infinity,
    maxX: -Infinity,
    maxY: -Infinity,
});

const boundsValid = (b) =>
    Number.isFinite(b.minX)
    && Number.isFinite(b.minY)
    && Number.isFinite(b.maxX)
    && Number.isFinite(b.maxY)
    && b.maxX > b.minX
    && b.maxY > b.minY;

/** Вершины LWPOLYLINE / POLYLINE без дублирующей замыкающей точки. */
const polylinePoints = (entity) => {
    const raw = (entity?.vertices || [])
        .map((v) => point(v.x, v.y))
        .filter((p) => isFiniteNum(p.x) && isFiniteNum(p.y));
    if (raw.length < 2) return [];
    const first = raw[0];
    const last = raw[raw.length - 1];
    if (nearlyEqual(first.x, last.x) && nearlyEqual(first.y, last.y) && raw.length > 2) {
        return raw.slice(0, -1);
    }
    return raw;
};

const isClosedPolyline = (entity, pts) => {
    if (entity?.shape) return pts.length >= 3;
    if (pts.length < 3) return false;
    const verts = entity?.vertices || [];
    if (verts.length >= 2) {
        const first = verts[0];
        const last = verts[verts.length - 1];
        if (
            nearlyEqual(Number(first.x), Number(last.x))
            && nearlyEqual(Number(first.y), Number(last.y))
        ) {
            return true;
        }
    }
    // Flag 1 on LWPOLYLINE often means closed; dxf-parser maps to `shape`.
    return Boolean(entity?.shape);
};

const collectEntities = (dxf) => {
    const list = [];
    if (Array.isArray(dxf?.entities)) {
        list.push(...dxf.entities);
    }
    const blocks = dxf?.blocks || {};
    Object.keys(blocks).forEach((name) => {
        // Model-space entities already in entities; skip *Model_Space duplicates if empty inserts-only.
        if (String(name).startsWith("*")) return;
        const ents = blocks[name]?.entities;
        if (Array.isArray(ents)) list.push(...ents);
    });
    return list;
};

const addEntityToBounds = (bounds, entity) => {
    const type = entity?.type;
    if (type === "LINE") {
        (entity.vertices || []).forEach((v) => expandBounds(bounds, v.x, v.y));
        return;
    }
    if (type === "LWPOLYLINE" || type === "POLYLINE") {
        polylinePoints(entity).forEach((p) => expandBounds(bounds, p.x, p.y));
        return;
    }
    if (type === "CIRCLE") {
        const r = Number(entity.radius) || 0;
        const cx = Number(entity.center?.x) || 0;
        const cy = Number(entity.center?.y) || 0;
        expandBounds(bounds, cx - r, cy - r);
        expandBounds(bounds, cx + r, cy + r);
        return;
    }
    if (type === "ARC") {
        const r = Number(entity.radius) || 0;
        const cx = Number(entity.center?.x) || 0;
        const cy = Number(entity.center?.y) || 0;
        // Conservative AABB of full circle (MVP).
        expandBounds(bounds, cx - r, cy - r);
        expandBounds(bounds, cx + r, cy + r);
        return;
    }
    if (type === "POINT" && entity.position) {
        expandBounds(bounds, entity.position.x, entity.position.y);
    }
};

const isFullCircleArc = (entity) => {
    if (entity?.type !== "ARC") return false;
    const len = Math.abs(Number(entity.angleLength));
    if (!Number.isFinite(len)) return false;
    // ~2π rad or ~360 deg depending on parser units — dxf-parser uses radians.
    return Math.abs(len - Math.PI * 2) < 0.05 || Math.abs(len - 360) < 0.5;
};

/**
 * @param {string} dxfText
 * @param {{name?: string, heightMm?: number}} [options]
 * @returns {{
 *   detailData: object,
 *   meta: {holeCount: number, contourSource: string, length: number, width: number, height: number}
 * }}
 */
export const parseDxfToDetailData = (dxfText, options = {}) => {
    const text = String(dxfText || "").trim();
    if (!text) {
        throw new Error("Пустой DXF-файл");
    }

    const parser = new DxfParser();
    let dxf;
    try {
        dxf = parser.parseSync(text);
    } catch (err) {
        throw new Error(err?.message || "Не удалось разобрать DXF");
    }
    if (!dxf) {
        throw new Error("Не удалось разобрать DXF");
    }

    const entities = collectEntities(dxf);
    if (!entities.length) {
        throw new Error("В DXF нет геометрических сущностей");
    }

    const closedPolylines = [];
    for (const entity of entities) {
        if (entity?.type !== "LWPOLYLINE" && entity?.type !== "POLYLINE") continue;
        const pts = polylinePoints(entity);
        if (!isClosedPolyline(entity, pts) || pts.length < 3) continue;
        closedPolylines.push({
            points: pts,
            area: polygonArea(pts),
        });
    }

    closedPolylines.sort((a, b) => b.area - a.area);

    let contourPoints = null;
    let contourSource = "aabb";
    if (closedPolylines[0] && closedPolylines[0].area > EPS) {
        contourPoints = closedPolylines[0].points.map((p) => ({...p}));
        contourSource = "polyline";
    }

    const bounds = emptyBounds();
    if (contourPoints) {
        contourPoints.forEach((p) => expandBounds(bounds, p.x, p.y));
    } else {
        entities.forEach((e) => addEntityToBounds(bounds, e));
    }

    if (!boundsValid(bounds)) {
        throw new Error("Не удалось определить габариты детали из DXF");
    }

    const originX = bounds.minX;
    const originY = bounds.minY;
    const length = bounds.maxX - bounds.minX;
    const width = bounds.maxY - bounds.minY;
    const height = Math.max(Number(options.heightMm) || DEFAULT_THICKNESS_MM, 1);

    if (length < 1 || width < 1) {
        throw new Error("Габариты детали слишком малы");
    }

    const shift = (p) => point(p.x - originX, p.y - originY);

    let detailContourPoints;
    if (contourPoints) {
        detailContourPoints = contourPoints.map(shift);
        const first = detailContourPoints[0];
        const last = detailContourPoints[detailContourPoints.length - 1];
        if (!nearlyEqual(first.x, last.x) || !nearlyEqual(first.y, last.y)) {
            detailContourPoints = [...detailContourPoints, {...first}];
        }
    } else {
        detailContourPoints = [
            {x: 0, y: 0},
            {x: length, y: 0},
            {x: length, y: width},
            {x: 0, y: width},
            {x: 0, y: 0},
        ];
    }

    const holes = [];
    let holeIndex = 0;
    for (const entity of entities) {
        let center = null;
        let radius = 0;
        if (entity?.type === "CIRCLE") {
            center = entity.center;
            radius = Number(entity.radius) || 0;
        } else if (isFullCircleArc(entity)) {
            center = entity.center;
            radius = Number(entity.radius) || 0;
        }
        if (!center || radius <= 0) continue;

        const cx = (Number(center.x) || 0) - originX;
        const cy = (Number(center.y) || 0) - originY;
        // Skip circles that look like the outer outline (≈ panel size).
        if (radius * 2 >= Math.min(length, width) * 0.9) continue;

        holeIndex += 1;
        holes.push({
            name: `hole ${holeIndex}`,
            center: {x: cx, y: cy, z: 0},
            radius,
            depth: height,
            contour: {position: "TOP"},
        });
    }

    const name = String(options.name || "DXF деталь").trim() || "DXF деталь";

    const detailData = {
        name,
        length,
        width,
        height,
        detailContour: {points: detailContourPoints},
        holes,
        source: "dxf",
    };

    return {
        detailData,
        meta: {
            holeCount: holes.length,
            contourSource,
            length,
            width,
            height,
        },
    };
};

export const DEFAULT_DXF_THICKNESS_MM = DEFAULT_THICKNESS_MM;
